/**
 * CreateDefinitionsBlock — Feature A (one-click "Create metafield
 * definitions in Shopify").
 *
 * Lives card-top in `ProductDataLookupCard.tsx`, above the SweepStatusBlock.
 * One namespace input + one button: provisions the CONTEXT-AWARE onboarding
 * preset (resolveOnboardingPreset — composition fields for design-ID orgs,
 * artwork fields for everyone else) as Shopify metafield definitions in the
 * org's live store, then fills the matching IMS mappings for purposes that
 * aren't mapped yet. The server resolves the same preset from the same
 * mapping list, so the dialog preview is authoritative.
 *
 * Behaviour rules (mirrors SweepStatusBlock's IdleBlock conventions):
 *   - `type="button"` everywhere — the card renders inside the parent
 *     OrganizationSettings `<form>`.
 *   - A confirmation AlertDialog echoes the namespace + the concrete plan
 *     (create N definitions; map M fields; K already-mapped untouched)
 *     before anything fires against the live store.
 *   - onSuccess invalidates BOTH `list` and `discoverMetafields` before any
 *     state reset, then shows ONE aggregate toast whose copy leads with
 *     failures if any. If any mapping was filled, the parent is told so it
 *     can open the Advanced disclosure (all preset purposes are advanced).
 *   - onError: persistent id-deduped toast. FORBIDDEN (missing
 *     write_products) additionally renders a persistent inline amber note
 *     and keeps the button disabled until the discovery query refetches
 *     (the operator's cue that the connection changed).
 *   - Below the button, a ResultBand summarises the last run: green all-ok /
 *     amber any-fail, `tabular-nums` counts, and a "View details" disclosure
 *     naming each failed purpose + its error.
 *
 * Explicit purpose list (optional `purposes` + `buttonLabel`): a surface that
 * needs a specific set of fields (the Platen size card's four platen info
 * fields) passes it here instead of building its own block. The list replaces
 * the onboarding preset in the plan, the copy and the mutation, which sends it
 * as the router's explicit `purposes` override. Absent, the block behaves
 * exactly as before (context-aware onboarding preset, default label).
 *
 * Onboarding wizard props (BLU-1376, all opt-in; Settings and the Platen card
 * pass none and render exactly as before):
 *   - `fixedNamespace`: no namespace box. The org's existing mapping
 *     namespace still wins (seeded as today); with no mappings the block
 *     sends `fixedNamespace`. The button waits for the mappings read so the
 *     namespace is settled before anything runs.
 *   - `embedded`: no card chrome and no intro copy (the step pane already
 *     says it); just the button, its notes, the result band and the confirm.
 *   - `buttonClassName`: the wizard's pane button look.
 *   - `onCreated`: runs after every successful run (not only when a mapping
 *     was filled), so the wizard can arm its advance.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import {
  COMPOSITION_ORG_ONBOARDING_PURPOSES,
  PRINTRUN_METAFIELD_NAMESPACE,
  resolveDefinitionsPreset,
  resolveOnboardingPreset,
} from '@threadheads/shared';
import { PURPOSE_SHORT_LABEL, type Purpose } from './purposes';
import { ResultBand } from './ResultBand';
import type { DiscoveryStatus } from './MetafieldPicker';
import { DEFAULT_DEFINITIONS_NAMESPACE, NS_KEY_REGEX, firstMappingNamespace } from './definitions-namespace';

// Toast IDs — deduplicated so repeated outcomes replace rather than stack.
const TOAST_ID = {
  createDefs: 'mapping-create-defs',
  createDefsError: 'mapping-create-defs-error',
} as const;

type MutationResult = {
  namespace: string;
  results: Array<{
    purpose: Purpose;
    key: string;
    ownerType: 'PRODUCT' | 'PRODUCTVARIANT';
    status: 'created' | 'existed' | 'failed';
    error: string | null;
    mappingFilled: boolean;
    mappingSkipped: boolean;
  }>;
  created: number;
  existed: number;
  failed: number;
  mappingsFilled: number;
  mappingsSkipped: number;
};

interface CreateDefinitionsBlockProps {
  admin: boolean;
  discoveryStatus: DiscoveryStatus;
  /** Called when the run filled at least one (advanced) mapping. */
  onAdvancedMappingsFilled?: () => void;
  /**
   * Refetches the card's discovery query — the recovery affordance after a
   * FORBIDDEN (the operator reconnects Shopify in the Integration card, then
   * clicks "Check again" here instead of reloading the page).
   */
  onRetryDiscovery: () => void;
  /**
   * Provision exactly these purposes instead of the onboarding preset. Sent
   * to `createDefinitions` as its explicit `purposes` list.
   */
  purposes?: readonly Purpose[];
  /** Button label override; defaults to "Create definitions in Shopify". */
  buttonLabel?: string;
  /**
   * One line of context under the body copy (already translated), for
   * example how many of the listed fields are still unmapped. Lives inside
   * the block so the surface has one heading, not a caption above a card.
   */
  note?: string;
  /** Hide the namespace box and use this namespace unless a mapping already has one. */
  fixedNamespace?: string;
  /** No card chrome and no intro copy (the onboarding step pane frames it). */
  embedded?: boolean;
  /** Extra classes for the run button (the onboarding pane button look). */
  buttonClassName?: string;
  /** Runs after every successful run. */
  onCreated?: () => void;
}

export function CreateDefinitionsBlock({
  admin,
  discoveryStatus,
  onAdvancedMappingsFilled,
  onRetryDiscovery,
  purposes,
  buttonLabel,
  note,
  fixedNamespace,
  embedded = false,
  buttonClassName,
  onCreated,
}: CreateDefinitionsBlockProps) {
  const utils = trpc.useUtils();
  // The block's own chrome and its confirm dialog are translated: the Platen
  // size card embeds it, and Thread Lab reads that card in zh.
  const { t } = useTranslation('settings');

  // Shares the card's cached list — used to pre-seed the namespace and to
  // compute the confirmation plan (map M / leave K unchanged).
  const mappingsQuery = trpc.shopifyMetafieldMappings.list.useQuery(undefined, {
    staleTime: 60_000,
  });
  const mappings = useMemo(() => mappingsQuery.data ?? [], [mappingsQuery.data]);

  const [namespace, setNamespace] = useState(fixedNamespace ?? DEFAULT_DEFINITIONS_NAMESPACE);
  // Pre-seed ONCE from an existing mapping's namespace when the list first
  // resolves — after that the operator's input is the only writer.
  const namespaceSeeded = useRef(false);
  useEffect(() => {
    if (namespaceSeeded.current || mappingsQuery.isLoading) return;
    namespaceSeeded.current = true;
    const seed = firstMappingNamespace(mappingsQuery.data);
    if (seed) setNamespace(seed);
  }, [mappingsQuery.isLoading, mappingsQuery.data]);

  const nsValid = NS_KEY_REGEX.test(namespace);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState<MutationResult | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Persistent inline note after a FORBIDDEN (missing write_products).
  // Cleared by the note's explicit "Check again" affordance (which also
  // refetches discovery) — a status-derived clear would never fire, because
  // an errored discovery query refetching does not pass back through the
  // first-load 'loading' state.
  const [scopeError, setScopeError] = useState<string | null>(null);

  const mutation = trpc.shopifyMetafieldMappings.createDefinitions.useMutation({
    onSuccess: async (result) => {
      // Invalidate BOTH reads BEFORE any state reset so the rows + dropdown
      // repaint from fresh data, not a stale cache.
      await Promise.all([
        utils.shopifyMetafieldMappings.list.invalidate(),
        utils.shopifyMetafieldMappings.discoverMetafields.invalidate(),
      ]);
      setLastResult(result);
      setDetailsOpen(false);

      const failedRows = result.results.filter((r) => r.status === 'failed');
      if (failedRows.length > 0) {
        // Lead with the failures; keep the toast persistent so a partial
        // outcome can't scroll away unnoticed.
        const names = failedRows.map((r) => PURPOSE_SHORT_LABEL[r.purpose]).join(', ');
        toast.error(
          `${failedRows.length} of ${result.results.length} definitions failed (${names}). ${result.created} created, ${result.existed} already existed. See details below the button.`,
          { id: TOAST_ID.createDefs, duration: Infinity },
        );
      } else {
        toast.success(
          `Shopify definitions ready under “${result.namespace}”: ${result.created} created, ${result.existed} already existed. ${result.mappingsFilled} mappings filled, ${result.mappingsSkipped} left as configured.`,
          { id: TOAST_ID.createDefs },
        );
      }
      if (result.mappingsFilled > 0) onAdvancedMappingsFilled?.();
      onCreated?.();
    },
    onError: (error) => {
      if (error.data?.code === 'FORBIDDEN') {
        const message =
          'Your Shopify connection can’t create definitions (missing write_products). Reconnect Shopify above to grant write_products, then use Check again.';
        setScopeError(message);
        toast.error(message, { id: TOAST_ID.createDefsError, duration: Infinity });
        return;
      }
      toast.error(`Couldn’t create definitions: ${error.message}`, {
        id: TOAST_ID.createDefsError,
        duration: Infinity,
      });
    },
  });

  // Context-aware preset + confirmation plan — resolved with the SAME shared
  // helper the backend service uses, from the same mapping list, so the
  // dialog previews exactly what the server will do: a design-ID org gets
  // the composition preset (shapes/variants/platens + shared fields),
  // everyone else the artwork preset, plus, under `printrun`, each preview
  // image field not mapped yet (BLU-1399, `resolveDefinitionsPreset`).
  const plan = useMemo(() => {
    const mapped = new Set<Purpose>(mappings.map((m) => m.purpose));
    const composition = !purposes && resolveOnboardingPreset(mapped) === COMPOSITION_ORG_ONBOARDING_PURPOSES;
    const preset: readonly Purpose[] = purposes ?? resolveDefinitionsPreset(mapped, namespace);
    const toMap = preset.filter((p) => !mapped.has(p));
    return {
      composition,
      // The namespace rule (S6), never a reference compare on the list, which varies with the mappings.
      printrun: !purposes && namespace === PRINTRUN_METAFIELD_NAMESPACE && !composition,
      total: preset.length,
      toMap: toMap.length,
      alreadyMapped: preset.length - toMap.length,
    };
  }, [mappings, purposes, namespace]);

  if (!admin) return null;

  const buttonDisabled =
    mutation.isPending ||
    !nsValid ||
    // A fixed namespace yields to an existing mapping's; wait until we know.
    (fixedNamespace !== undefined && mappingsQuery.isLoading) ||
    discoveryStatus === 'errored' ||
    scopeError !== null;

  const failedRows = lastResult?.results.filter((r) => r.status === 'failed') ?? [];

  return (
    <div className={embedded ? 'space-y-3' : 'rounded-md border border-border bg-muted/20 px-3 py-3 space-y-3'}>
      {!embedded && (
        <div className="space-y-1">
          <p className="text-sm font-medium">{t('metafieldDefs.title')}</p>
          <p className="text-sm text-muted-foreground">
            {purposes ? (
              t('metafieldDefs.purposesBody', {
                fields: purposes.map((p) => PURPOSE_SHORT_LABEL[p]).join(', '),
              })
            ) : plan.composition ? (
              <>
                Creates the standard IMS metafield definitions in your Shopify
                store (print shapes, variants, platen sizes, custom print SKU,
                fulfilment type, pretreat) and maps them here in one step.
                Fields you&rsquo;ve already mapped are left unchanged.
              </>
            ) : plan.printrun ? (
              <>
                Creates the standard PrintRun metafield definitions in your Shopify
                store (artwork files, custom print SKU, fulfilment type, pretreat,
                front and back preview images) and maps them here in one step.
                Fields you&rsquo;ve already mapped are left unchanged.
              </>
            ) : (
              <>
                Creates the standard IMS metafield definitions in your Shopify
                store (artwork files, custom print SKU, fulfilment type,
                pretreat) and maps them here in one step. Fields you&rsquo;ve
                already mapped are left unchanged.
              </>
            )}
          </p>
          {note ? <p className="text-xs text-muted-foreground tabular-nums">{note}</p> : null}
        </div>
      )}

      <div
        className={
          embedded && fixedNamespace !== undefined
            ? 'flex flex-wrap items-center justify-end gap-3'
            : 'flex flex-col gap-3 sm:flex-row sm:items-end'
        }
      >
        {fixedNamespace === undefined && (
          <div className="space-y-1 sm:max-w-[16rem] flex-1">
            <Label htmlFor="create-defs-namespace" className="text-xs">
              {t('metafieldDefs.namespace')}
            </Label>
            <Input
              id="create-defs-namespace"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value.trim().toLowerCase())}
              placeholder="custom"
              disabled={mutation.isPending}
              aria-invalid={!nsValid}
              aria-describedby={nsValid ? undefined : 'create-defs-namespace-help'}
              className="font-mono"
            />
            {!nsValid && (
              <p id="create-defs-namespace-help" className="text-xs text-destructive">
                {t('metafieldDefs.namespaceHelp')}
              </p>
            )}
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setConfirmOpen(true)}
          disabled={buttonDisabled}
          className={cn('shrink-0 motion-safe:transition-all motion-reduce:transition-none', buttonClassName)}
        >
          {mutation.isPending ? (
            <>
              <Loader2
                className="mr-2 h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              {t('metafieldDefs.creating')}
            </>
          ) : (
            <>
              <Wand2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              {buttonLabel ?? t('metafieldDefs.defaultButton')}
            </>
          )}
        </Button>
      </div>

      {discoveryStatus === 'errored' && !scopeError && (
        <p className="text-xs text-muted-foreground">{t('metafieldDefs.discoveryErrored')}</p>
      )}

      {scopeError && (
        <p
          role="status"
          aria-live="polite"
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100"
        >
          {scopeError}{' '}
          <button
            type="button"
            onClick={() => {
              // Re-arm the button and refresh discovery; if the scope is
              // still missing, the next create attempt re-raises the note.
              setScopeError(null);
              onRetryDiscovery();
            }}
            className="underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded text-amber-900 dark:text-amber-100"
          >
            Check again
          </button>
        </p>
      )}

      {lastResult && (
        <ResultBand
          key={`${lastResult.namespace}-${lastResult.created}-${lastResult.existed}-${lastResult.failed}`}
          kind={lastResult.failed > 0 ? 'amber' : 'green'}
        >
          <p>
            <span className="tabular-nums">{lastResult.created}</span> created,{' '}
            <span className="tabular-nums">{lastResult.existed}</span> already
            existed
            {lastResult.failed > 0 ? (
              <>
                , <span className="tabular-nums">{lastResult.failed}</span>{' '}
                failed
              </>
            ) : null}
            . Mappings: <span className="tabular-nums">{lastResult.mappingsFilled}</span>{' '}
            filled, <span className="tabular-nums">{lastResult.mappingsSkipped}</span>{' '}
            left as configured.
            {failedRows.length > 0 && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
                  aria-expanded={detailsOpen}
                >
                  {detailsOpen ? 'Hide details' : 'View details'}
                </button>
              </>
            )}
          </p>
          {detailsOpen && failedRows.length > 0 && (
            <ul className="mt-1 space-y-1 rounded border border-amber-300 bg-amber-100 px-2 py-1 text-xs dark:border-amber-800 dark:bg-amber-900/40">
              {failedRows.map((row) => (
                <li key={row.purpose} className="break-all">
                  <span className="font-medium">{PURPOSE_SHORT_LABEL[row.purpose]}</span>
                  {': '}
                  {row.error ?? 'Unknown error.'}
                </li>
              ))}
            </ul>
          )}
        </ResultBand>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('metafieldDefs.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="tabular-nums">
              <Trans
                t={t}
                i18nKey="metafieldDefs.confirmBody"
                // Two counts in one sentence: each is its own `_one`/`_other`
                // phrase ("1 field", "3 fields"), resolved before Trans
                // places it.
                values={{
                  total: plan.total,
                  namespace,
                  toMapFields: t('metafieldDefs.toMapFields', { count: plan.toMap }),
                  alreadyMappedFields: t('metafieldDefs.alreadyMappedFields', { count: plan.alreadyMapped }),
                }}
                components={[<span key="ns" className="font-mono text-foreground" />]}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">{t('metafieldDefs.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={() => {
                setConfirmOpen(false);
                mutation.mutate(
                  purposes ? { namespace, purposes: [...purposes] } : { namespace },
                );
              }}
            >
              {t('metafieldDefs.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
